'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { cn } from '../../lib/utils';
import { Icon } from '@iconify/react';
import { useState, useEffect } from 'react';
import { useTheme } from 'next-themes';
import { useTranslation } from 'react-i18next';

// Define navigation items with translation keys
const navItems = [
  { name: 'home', href: '/', icon: 'lucide:home' },
  { name: 'projects', href: '/projects', icon: 'lucide:folder' },
  { name: 'agents', href: '/agents', icon: 'lucide:bot' },
  { name: 'about', href: '/about', icon: 'lucide:info' },
  { name: 'contact', href: '/contact', icon: 'lucide:mail' },
];

/**
 * Navbar component
 * Provides a clean, simple navigation bar for all pages
 * Especially useful for project detail pages where a sidebar is not wanted
 */
export function Navbar() {
  const { t } = useTranslation();
  const pathname = usePathname();
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Set mounted to true on client side
  useEffect(() => {
    setMounted(true);

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={cn(
      "sticky top-0 z-40 transition-all duration-200",
      isScrolled
        ? "bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200/80 dark:border-slate-800/80 py-3"
        : "bg-transparent py-4"
    )}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between">
          {/* Logo/Home Link */}
          <Link href="/" className="flex items-center gap-2 font-semibold text-slate-900 dark:text-white">
            <Icon icon="lucide:code" className="h-5 w-5" />
            <span>Portfolio</span>
          </Link>

          {/* Main Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => {
              const isActive = pathname === item.href ||
                (item.href !== '/' && pathname.startsWith(item.href));

              return (
                <Link
                  key={item.href}
                  href={item.href}
                  prefetch={false}
                  className={cn(
                    "px-3 py-2 rounded-md text-sm font-medium transition-colors relative group",
                    isActive
                      ? "text-slate-900 dark:text-white"
                      : "text-slate-700 hover:text-slate-900 dark:text-slate-200 dark:hover:text-white"
                  )}
                >
                  {isActive && (
                    <span
                      className="absolute inset-0 bg-slate-100 dark:bg-slate-800 rounded-md z-0 transition-all duration-300 shadow-sm"
                    />
                  )}
                  <span className="relative z-10 flex items-center gap-1.5 font-bold uppercase tracking-wider text-[11px]">
                    {t(`Navbar.${item.name}`)}
                  </span>
                </Link>
              );
            })}
          </nav>

          {/* Theme Toggle & CTA */}
          <div className="flex items-center gap-4">
            <button
              data-cal-namespace="30min"
              data-cal-link="ellis-caicedo-hfdo7f/30min"
              data-cal-config='{"layout":"month_view","useSlotsViewOnSmallScreen":"true"}'
              className="hidden sm:flex items-center gap-2 px-5 py-2.5 bg-[#181A1D] dark:bg-white text-white dark:text-[#181A1D] rounded-full text-sm font-bold hover:scale-105 transition-all duration-300 shadow-lg shadow-black/10 dark:shadow-white/5 active:scale-95"
            >
              <span>{t('Navbar.scheduleCall')}</span>
              <Icon icon="solar:arrow-right-up-linear" width="18" height="18" />
            </button>

            <button
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              className={cn(
                "p-2.5 rounded-full transition-colors",
                "bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700"
              )}
              aria-label="Toggle theme"
            >
              {mounted && theme === 'dark' ? (
                <Icon icon="lucide:sun" className="h-5 w-5 text-yellow-400" />
              ) : (
                <Icon icon="lucide:moon" className="h-5 w-5 text-slate-700" />
              )}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
} 